import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { selectAllEntities } from '@ngneat/elf-entities';
import { CommonModule } from '@angular/common';
import { Message, MessageStore } from '../../../state/messages.repository';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DomSanitizer } from '@angular/platform-browser';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faFaceSmilePlus } from '@fortawesome/pro-duotone-svg-icons/faFaceSmilePlus';
import { faReply } from '@fortawesome/pro-duotone-svg-icons/faReply';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { TooltipDirective } from '../../../shared/components/ui/tooltip/tooltip.directive';
import { RelativeTimePipe } from '../../../shared/pipes/relative-time.pipe';
import { BlobUrlPipe } from '../../../shared/pipes/blob-url.pipe';
import { UserService } from '../../../core/services/user.service';
import { User, UserStore } from '../../../state/user.repository';
import { renderMessageHtml } from '../../../shared/helpers/message-html';
import { resolveAvatar } from '../../../shared/helpers/avatar';
import { RoomFileCard } from '../../files/room-file-card/room-file-card';
import { LanguageService } from '../../../core/services/language.service';

@Component({
  selector: 'andor-chat-msg',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    FontAwesomeModule,
    TooltipDirective,
    RelativeTimePipe,
    BlobUrlPipe,
    RoomFileCard,
  ],
  templateUrl: './chat-msg.html',
  styleUrl: './chat-msg.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Mirror layout/origin onto the host so SCSS can scope on `:host(.layout-split)`
  // / `:host(.is-self)` without `:host-context` (more reliable for class-change
  // reactivity). Reading the signals here makes the host bindings reactive
  // without needing per-CD getter calls.
  host: {
    '[class.layout-split]': 'layout() === "split"',
    '[class.is-self]': 'message().origin === "self"',
  },
})
export class ChatMsg {
  // Resolves a message sender's avatar: their custom avatar if set, else a
  // locally generated default derived from the sender's name.
  protected readonly resolveAvatar = resolveAvatar;
  protected readonly faFaceSmilePlus = faFaceSmilePlus;
  protected readonly faReply = faReply;
  protected readonly faXmark = faXmark;
  protected readonly faLock = faLock;
  message = input.required<Message>();
  viewImage = input.required<(_: MouseEvent) => void>();
  // Chat-wide layout mode forwarded from Chat.
  layout = input<'left' | 'split'>('left');

  // Outputs consumed by Chat. Reactions and replies share a hover
  // affordance on the message bubble; quote-clicks come from the reply
  // preview block above replies. `reactRequested` carries the trigger button
  // so the parent's shared emoji-picker menu can anchor to it — only one
  // picker lives in the DOM regardless of message count.
  reactionToggled = output<{ message: Message; emoji: string }>();
  reactRequested = output<{
    message: Message;
    anchor: HTMLElement;
    xPosition: 'before' | 'after';
  }>();
  replyRequested = output<Message>();
  quoteClicked = output<string>();

  // The query also updates when a recycled system row becomes a user message.
  private readonly textContainer = viewChild<ElementRef<HTMLElement>>('textContainer');
  private readonly _copyTimers = new Map<HTMLButtonElement, ReturnType<typeof setTimeout>>();
  // Read before `instant()` so labels re-translate when the language changes.
  protected readonly lang = inject(LanguageService).active;

  constructor(
    protected sanitizer: DomSanitizer,
    private _translateService: TranslateService,
    private _userService: UserService,
    private _userStore: UserStore,
    private _messageStore: MessageStore
  ) {
    afterRenderEffect({
      write: (onCleanup) => {
        // innerHTML replaces the controls when virtual scrolling reuses this
        // component. Populate the new buttons after each HTML update.
        this.textHtml();
        this.lang();
        this.textContainer()
          ?.nativeElement.querySelectorAll('button.copy')
          .forEach((button) => {
            button.textContent = this._translateService.instant('Copy');
          });
        onCleanup(() => {
          this._copyTimers.forEach((timer) => clearTimeout(timer));
          this._copyTimers.clear();
        });
      },
    });
  }

  protected async copyCode(event: MouseEvent): Promise<void> {
    const container = this.textContainer()?.nativeElement;
    const button = event.target instanceof Element ? event.target.closest('button.copy') : null;
    if (!(button instanceof HTMLButtonElement) || !container?.contains(button)) return;
    const code = button.nextElementSibling;
    if (!code?.matches('.scroller')) return;

    try {
      await navigator.clipboard.writeText(code.textContent ?? '');
    } catch {
      return;
    }
    // The row may have been recycled while clipboard access was pending.
    if (!button.isConnected) return;
    clearTimeout(this._copyTimers.get(button));
    button.textContent = this._translateService.instant('Copied!');
    this._copyTimers.set(
      button,
      setTimeout(() => {
        button.textContent = this._translateService.instant('Copy');
        this._copyTimers.delete(button);
      }, 2000)
    );
  }

  showBundleAvatar = computed(() => {
    const m = this.message();
    return !m.isBundled || !!m.isFirstInBundle;
  });

  showBundleName = computed(() => {
    const m = this.message();
    if (m.kind === 'mute') return false;
    return !m.isBundled || !!m.isFirstInBundle;
  });

  hasImageAttachment = computed(() => {
    const m = this.message();
    return m.kind === 'message' && m.payload.attachment?.type === 'image';
  });

  // Rendered HTML for user-message text. Storage holds the raw markdown
  // source — rendering happens here so total compute scales with messages
  // *viewed*, not messages *received*, and storage doesn't carry duplicate
  // render artifacts. The helper memoizes by source string, so multiple
  // chat-msg instances rendering the same message (incl. CDK virtual-
  // scroll view recycling) share work. Sanitization stays inside the
  // Angular context here; the helper itself is a pure string→string.
  textHtml = computed(() => {
    const m = this.message();
    if (m.kind !== 'message') return this.sanitizer.bypassSecurityTrustHtml('');
    const source = m.payload.text ?? '';
    return this.sanitizer.bypassSecurityTrustHtml(renderMessageHtml(source));
  });

  pokeText = computed(() => {
    this.lang();
    const m = this.message();
    if (m.origin === 'self') {
      const name = m.receiver?.name || this._translateService.instant('someone');
      return this._translateService.instant('You poked [name]', { name });
    }
    return this._translateService.instant('[name] poked you', {
      name: m.sender.name,
    });
  });

  // Tooltip for the "Private" chip. Both the sender and the receiver render
  // this same template, so the wording flips on `origin`: a DM I sent reads
  // "Private message to <receiver>"; one I received reads "Private message
  // from <sender>". The receiver is always the own user on the receive side,
  // so `scope === 'private'` (not the presence of `receiver`) is the signal.
  privateTooltip = computed(() => {
    this.lang();
    const m = this.message();
    if (m.origin === 'self') {
      const name = m.receiver?.name || this._translateService.instant('someone');
      return this._translateService.instant('Private message to [name]', { name });
    }
    return this._translateService.instant('Private message from [name]', {
      name: m.sender.name,
    });
  });

  // System messages use the same 'message' kind in ChatService, so payload.text is the
  // common case. Fallback handles stringly-typed payloads (pokes/mutes) if a system
  // message is ever routed through those kinds in the future.
  systemText = computed(() => {
    const m = this.message();
    if (m.kind === 'message') return m.payload.text || '';
    return typeof m.payload === 'string' ? m.payload : '';
  });

  // -- Interaction affordances --------------------------------------------

  // System messages can't be reacted to or replied to (no target identity,
  // and reactions/replies don't make sense on aggregated system notices).
  // File cards render outside the bubble, so they carry no reaction bar either.
  canInteract = computed(() => {
    const m = this.message();
    return m.origin !== 'system' && m.kind !== 'file';
  });

  // Compact aggregation of `message.reactions` for the chip row. Order is
  // insertion (first emoji used appears first), preserving the natural feel
  // of "in order of who reacted first".
  private _reactionGroups = computed<Array<{ emoji: string; count: number; userIds: string[] }>>(
    () => {
      const reactions = this.message().reactions ?? [];
      const byEmoji = new Map<string, string[]>();
      for (const r of reactions) {
        const list = byEmoji.get(r.emoji);
        if (list) list.push(r.userId);
        else byEmoji.set(r.emoji, [r.userId]);
      }
      return Array.from(byEmoji, ([emoji, userIds]) => ({
        emoji,
        count: userIds.length,
        userIds,
      }));
    }
  );

  // Lookup map from userId → display name, sourced from UserStore. Recomputed
  // only when the user-entities collection changes — used to build per-chip
  // tooltips in `reactionDisplay` below. Replaces the per-CD O(reactions ×
  // users) scan that the old `reactorNames` getter did.
  private _userEntities = toSignal(this._userStore.store.pipe(selectAllEntities()), {
    initialValue: [] as User[],
  });

  private _userIdToName = computed(() => {
    const map = new Map<string, string>();
    for (const u of this._userEntities()) {
      if (u?.id) map.set(u.id, u.data.name);
    }
    return map;
  });

  // Display-ready reaction chips: pre-computes `isOwn` and the tooltip string
  // so the template doesn't call methods on every CD pass. Falls back to the
  // raw userId when the user isn't currently in the store (left the room, or
  // never observed during this session). Own user reads as "You" to match
  // the chat convention.
  reactionDisplay = computed<
    Array<{ emoji: string; count: number; isOwn: boolean; tooltip: string }>
  >(() => {
    this.lang();
    const ownId = this._userService.ownUser()?.id;
    const nameMap = this._userIdToName();
    const youLabel = this._translateService.instant('You');
    return this._reactionGroups().map((g) => ({
      emoji: g.emoji,
      count: g.count,
      isOwn: !!ownId && g.userIds.includes(ownId),
      tooltip: g.userIds
        .map((uid) => (uid === ownId ? youLabel : (nameMap.get(uid) ?? uid)))
        .join('\n'),
    }));
  });

  toggleReaction(emoji: string): void {
    this.reactionToggled.emit({ message: this.message(), emoji });
  }

  onReplyClick(event: MouseEvent): void {
    event.stopPropagation();
    this.replyRequested.emit(this.message());
  }

  // For the split-layout own-bubble case, the action bar sits on the
  // bubble's left side; opening a menu that extends rightward from there
  // feels more natural. Default elsewhere is "before" (extending leftward
  // from the trigger's right edge). The context-menu component already
  // viewport-flips when there isn't space.
  onReactClick(event: MouseEvent): void {
    event.stopPropagation();
    const message = this.message();
    const xPosition: 'before' | 'after' =
      this.layout() === 'split' && message.origin === 'self' ? 'after' : 'before';
    this.reactRequested.emit({
      message,
      anchor: event.currentTarget as HTMLElement,
      xPosition,
    });
  }

  // -- Quote preview ------------------------------------------------------

  quotedMessage = computed(() => {
    const m = this.message();
    if (m.kind !== 'message') return undefined;
    return m.payload.quotedMessage;
  });

  // True when the quoted original is locally available (so click → scroll is
  // meaningful). When false, the preview still renders the snippet, the
  // cursor reflects the non-clickability, and a tooltip explains. Kept as a
  // getter because `MessageStore.has` isn't signal-backed; reactivity to
  // late-arriving originals is acceptable to lose for v1 (the preview only
  // gets re-evaluated when the row's own CD ticks, which is rare with signal
  // inputs and OnPush).
  get quotedOriginalAvailable(): boolean {
    const q = this.quotedMessage();
    return !!q && this._messageStore.has(q.id);
  }

  onQuoteClick(event: MouseEvent): void {
    event.stopPropagation();
    const q = this.quotedMessage();
    if (q && this.quotedOriginalAvailable) this.quoteClicked.emit(q.id);
  }
}
